// Lightweight, dependency-free canvas confetti — a short brand-colored burst.
//
// We hand-roll this rather than pull in a library (dependency-hygiene rule) and
// it MUST respect prefers-reduced-motion: when the user asks for reduced motion
// it is a no-op, matching the global motion policy in global.css. Colors come
// from the brand palette (lime + pastels); no emoji, no imagery.

// Brand tokens (mirrors --lime / --lime-light / --aqua / --pink / --orange / --cream).
const BRAND_COLORS = ['#BEE034', '#D4F56A', '#A6EFFF', '#FFA6EA', '#FEB000', '#FFFDD8'];

export interface ConfettiOptions {
  /** Number of particles. Default 80. */
  particleCount?: number;
  /** Launch origin as a fraction of the viewport (0..1). Default centre-upper. */
  originX?: number;
  originY?: number;
  /** Cone spread in degrees. Default 100. */
  spread?: number;
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  rot: number;
  vr: number;
  size: number;
  color: string;
  round: boolean;
}

export function fireConfetti(opts: ConfettiOptions = {}): void {
  if (typeof window === 'undefined') return;
  // Reduced-motion: skip the animation entirely.
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

  const count = opts.particleCount ?? 80;
  const originX = (opts.originX ?? 0.5) * window.innerWidth;
  const originY = (opts.originY ?? 0.35) * window.innerHeight;
  const spread = ((opts.spread ?? 100) * Math.PI) / 180;

  const canvas = document.createElement('canvas');
  const dpr = window.devicePixelRatio || 1;
  canvas.width = window.innerWidth * dpr;
  canvas.height = window.innerHeight * dpr;
  canvas.style.cssText =
    'position:fixed;inset:0;width:100%;height:100%;pointer-events:none;z-index:100';
  document.body.appendChild(canvas);

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    canvas.remove();
    return;
  }
  ctx.scale(dpr, dpr);

  const particles: Particle[] = [];
  for (let i = 0; i < count; i++) {
    const angle = -Math.PI / 2 + (Math.random() - 0.5) * spread;
    const velocity = 6 + Math.random() * 8;
    particles.push({
      x: originX,
      y: originY,
      vx: Math.cos(angle) * velocity,
      vy: Math.sin(angle) * velocity,
      rot: Math.random() * Math.PI,
      vr: (Math.random() - 0.5) * 0.4,
      size: 6 + Math.random() * 6,
      color: BRAND_COLORS[Math.floor(Math.random() * BRAND_COLORS.length)]!,
      round: Math.random() < 0.5,
    });
  }

  const gravity = 0.28;
  const drag = 0.995;
  const maxFrames = 180;
  const w = window.innerWidth;
  const h = window.innerHeight;
  let frame = 0;

  const tick = () => {
    frame++;
    ctx.clearRect(0, 0, w, h);
    const alpha = Math.max(0, 1 - frame / maxFrames);
    for (const p of particles) {
      p.vx *= drag;
      p.vy = p.vy * drag + gravity;
      p.x += p.vx;
      p.y += p.vy;
      p.rot += p.vr;
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rot);
      ctx.fillStyle = p.color;
      if (p.round) {
        ctx.beginPath();
        ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
        ctx.fill();
      } else {
        ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
      }
      ctx.restore();
    }
    if (frame < maxFrames) {
      requestAnimationFrame(tick);
    } else {
      canvas.remove();
    }
  };
  requestAnimationFrame(tick);
}
